import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { authActions } from '../../stores/authStore';
import { loadCompanyData } from '../../utils/loadData';
import { PasswordField } from '../../components/PasswordField';
import type { InviteInfo } from 'shared/types/auth';

export const Route = createFileRoute('/accept-invite/')({
  component: AcceptInvitePage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || '',
  }),
});

function AcceptInvitePage() {
  const { token } = useSearch({ from: '/accept-invite/' });
  const navigate = useNavigate();

  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [infoError, setInfoError] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Load invite info on mount
  useEffect(() => {
    if (!token) {
      setInfoError('No invite token provided. Please use the link from your email.');
      setLoadingInfo(false);
      return;
    }

    authActions
      .getInviteInfo(token)
      .then((info) => {
        setInviteInfo(info as InviteInfo);
      })
      .catch((err: Error) => {
        setInfoError(err.message || 'Invalid or expired invite link');
      })
      .finally(() => {
        setLoadingInfo(false);
      });
  }, [token]);

  const isExistingUser = inviteInfo?.existingUser === true;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!isExistingUser && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authActions.acceptInvite({
        token,
        password,
        ...(isExistingUser ? {} : { firstName, lastName }),
      });
      loadCompanyData(); // Non-blocking — load data in background
      navigate({ to: '/' });
    } catch (err: unknown) {
      const error = err as Error & { details?: string[] };
      if (error.details) {
        setError(error.details.join('. '));
      } else {
        setError(error.message || 'Failed to accept invite');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/certain-logo.png"
            alt="Certain®"
            className="h-16 object-contain mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-900">
            {isExistingUser ? 'Join Company' : 'Accept Invite'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isExistingUser
              ? 'Enter your password to join the company'
              : 'Set up your account to get started'}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {loadingInfo ? (
            <div className="text-center py-8">
              <div className="inline-block w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
              <p className="text-gray-500 mt-4">Loading invite details...</p>
            </div>
          ) : infoError ? (
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Invalid Invite</h2>
              <p className="text-gray-500 text-sm">{infoError}</p>
              <Link
                to="/login"
                className="inline-block mt-4 text-purple-600 hover:text-purple-700 font-medium text-sm"
              >
                Go to sign in
              </Link>
            </div>
          ) : inviteInfo ? (
            <>
              {/* Invite details banner */}
              <div className="bg-purple-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-purple-900">
                  {isExistingUser ? (
                    <>
                      You already have an account. <strong>{inviteInfo.inviterName}</strong> has invited you
                      to join <strong>{inviteInfo.companyName}</strong> as a{' '}
                      <strong>{inviteInfo.role === 'company_admin' ? 'Company Administrator' : 'Team Member'}</strong>
                      {inviteInfo.permission === 'edit' ? ' with edit access' : ' with view-only access'}.
                      Enter your password to confirm.
                    </>
                  ) : (
                    <>
                      <strong>{inviteInfo.inviterName}</strong> invited you to join{' '}
                      <strong>{inviteInfo.companyName}</strong> as a{' '}
                      <strong>{inviteInfo.role === 'company_admin' ? 'Company Administrator' : 'Team Member'}</strong>
                      {inviteInfo.permission === 'edit' ? ' with edit access' : ' with view-only access'}.
                    </>
                  )}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <p className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 text-sm">
                    {inviteInfo.email}
                  </p>
                </div>

                {/* Only show name fields for new users */}
                {!isExistingUser && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        required
                        autoComplete="given-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        required
                        autoComplete="family-name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    {isExistingUser ? 'Your Password' : 'Password'}
                  </label>
                  <PasswordField
                    id="password"
                    required
                    autoComplete={isExistingUser ? 'current-password' : 'new-password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                    placeholder={isExistingUser ? 'Enter your existing password' : 'Min 8 chars, 1 upper, 1 lower, 1 number'}
                  />
                </div>

                {!isExistingUser && (
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password
                    </label>
                    <PasswordField
                      id="confirmPassword"
                      required
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 px-4 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                  style={{ background: 'var(--color-accent)' }}
                >
                  {loading
                    ? (isExistingUser ? 'Joining company...' : 'Setting up account...')
                    : (isExistingUser ? `Join ${inviteInfo.companyName}` : 'Create Account & Join')}
                </button>
              </form>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
